import { z } from 'zod';
import { extensionEntrySchema, publisherNameSchema, stripExtensionListUiPlaceholders, } from './extension-validators';
import { severitySchema } from './shared/severity';
import { extensionSecuritySettingsSchema } from './extension-security';
/*
  Generic policy contracts that can be reused for user policies and organization policies
*/
/*
  PolicyMode enum - the 3-way toggle state for each policy
*/
export var PolicyMode;
(function (PolicyMode) {
    PolicyMode["Off"] = "off";
    PolicyMode["Monitor"] = "monitor";
    PolicyMode["Enforce"] = "enforce";
})(PolicyMode || (PolicyMode = {}));
export const policyModeSchema = z.enum([
    PolicyMode.Off,
    PolicyMode.Monitor,
    PolicyMode.Enforce,
]);
/*
  PolicyKey enum - registry of all available policies

  ⚠️ ADDING OR CHANGING A POLICY? Update its detection-key classification in
  `apps/backend/src/domain/scans/detection-key.ts` → POLICY_INPUT_PROFILE
  (KIR-3973). That map decides when a policy's cached verdict is invalidated on
  a rules/config change; it is keyed `Record<PolicyKey, …>`, so a NEW policy is a
  compile error until you classify it. For a CHANGED policy (e.g. a new setting
  that affects detection), re-verify its `usesRuleset` / `usesOrgConfig` flags
  against the scanner — an over-narrow profile serves a STALE verdict. A tripwire
  test in `detection-key.test.ts` also fails when this enum changes.
*/
export var PolicyKey;
(function (PolicyKey) {
    PolicyKey["HiddenCharacters"] = "hidden-characters";
    PolicyKey["McpSecurity"] = "mcp-security";
    PolicyKey["CursorDatabase"] = "cursor-database";
    PolicyKey["YoloModes"] = "yolo-modes";
    PolicyKey["McpServerSources"] = "mcp-server-sources";
    PolicyKey["ExtensionSecurity"] = "extension-security";
    PolicyKey["ExtensionsManagement"] = "extensions-management";
    PolicyKey["AiInstructions"] = "ai-instructions";
    PolicyKey["SecretDetection"] = "secret-detection";
    PolicyKey["AiInstructionCatalog"] = "ai-instruction-catalog";
    PolicyKey["AiAgentInstructions"] = "ai-agent-instructions";
    // KIR-3925: scanner-less mode policy of the Threat Modeling catalog page.
    PolicyKey["ThreatModeling"] = "threat-modeling";
    PolicyKey["PiiDetection"] = "pii-detection";
    // KIR-4443: typosquatted / known-malicious package installs by coding agents.
    PolicyKey["MaliciousPackages"] = "malicious-packages";
    // KIR-4443: Allow/Block list for packages, the configuration-category peer of
    // the detection policy above.
    PolicyKey["PackagesManagement"] = "packages-management";
    // KIR-4699: Block list for agent skills, managed only from Inventory →
    // Skills. Hidden from the Policies page and fixed at Enforce, like Packages.
    PolicyKey["SkillsManagement"] = "skills-management";
})(PolicyKey || (PolicyKey = {}));
/**
 * Route param validation for `:policyKey`. Explicit tuple so `ai-instructions` is always
 * included (avoids occasional bundler/enum edge cases with `z.enum(PolicyKey)` alone).
 */
export const POLICY_KEY_VALUES = [
    PolicyKey.HiddenCharacters,
    PolicyKey.McpSecurity,
    PolicyKey.CursorDatabase,
    PolicyKey.YoloModes,
    PolicyKey.McpServerSources,
    PolicyKey.ExtensionSecurity,
    PolicyKey.ExtensionsManagement,
    PolicyKey.AiInstructions,
    PolicyKey.SecretDetection,
    PolicyKey.AiInstructionCatalog,
    PolicyKey.AiAgentInstructions,
    PolicyKey.ThreatModeling,
    PolicyKey.PiiDetection,
    PolicyKey.MaliciousPackages,
    PolicyKey.PackagesManagement,
    PolicyKey.SkillsManagement,
];
export const policyKeySchema = z.enum(POLICY_KEY_VALUES);
/**
 * Setting keys that are hidden from the policy settings UI and list API.
 * These are controlled by the Off/Monitor/Enforce selector instead.
 * Keep in sync when adding or renaming prevention-related settings.
 */
export const PREVENTION_SETTING_KEYS = {
    'extension-security': [
        'autoRemoveVulnerableExtensions',
        'removalSeverityThreshold',
    ],
    'extensions-management': ['autoRemoveBlockedExtensions'],
    'secret-detection': ['preventSecretLeakage'],
};
/*
  settingKeySchema validates a settingKey parameter.
  It must be a URL-safe string containing only alphanumeric characters, hyphens, and underscores.
  This ensures it's a single path element and prevents path traversal attacks.
  Example: "blockedListMode", "excessiveCommandsThreshold", "aiCodeSensitivity"
*/
export const settingKeySchema = z
    .string()
    .min(1, 'Setting key cannot be empty')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Setting key must be URL-safe (alphanumeric, hyphens, underscores only)');
/*
  Policy entity schemas
*/
/*
  Why the Enforce mode is unavailable for a policy, so the UI can show the
  correct tooltip:
  - 'feature_flag': the policy supports Enforce by design, but the
    enforce_policy_mode feature flag is off → "Contact Support to enable Enforce".
  - 'unsupported': the policy does not support Enforce by design → "Coming Soon".
  Absent when Enforce is available.
*/
export const ENFORCE_DISABLED_REASONS = {
    FeatureFlag: 'feature_flag',
    Unsupported: 'unsupported',
};
export const enforceDisabledReasonSchema = z.enum([
    ENFORCE_DISABLED_REASONS.FeatureFlag,
    ENFORCE_DISABLED_REASONS.Unsupported,
]);
/**
 * Policy category key → the group heading the product shows for it.
 *
 * **The single source of truth for these labels** (KIR-4432). They were
 * previously written out three times — `buildPolicyGroups()` in the backend
 * registry, `POLICY_CATEGORIES_META` in the frontend, and nowhere at all in the
 * public `/api/v1` contract, which published the bare key. The keys are far
 * enough from the labels that a customer cannot bridge the gap:
 * `configuration-policies` is shown as "Allow/Block Lists".
 *
 * Keys are the string values of the backend's `PolicyCategory` enum. That enum
 * lives in `apps/backend/src/types/index.ts` and cannot be imported here
 * (api-client must not depend on the backend), so this map is keyed by the raw
 * strings and the backend registry asserts against it by using it.
 */
export const POLICY_CATEGORY_LABELS = Object.freeze({
    'attack-detection-policies': 'Detection Policies',
    'configuration-policies': 'Allow/Block Lists',
    'ai-instruction-policies': 'AI Agent Instructions',
    'secure-ai-coding-policies': 'Secure AI Coding Enforcement',
    'threat-modeling-policies': 'Threat Modeling',
});
/**
 * The display label for a policy category, falling back to the key itself.
 *
 * A category with no label is a new one someone added without touching this map;
 * echoing the key beats publishing `null`, because the key is at least
 * recognisable.
 */
export function getPolicyCategoryLabel(category) {
    return POLICY_CATEGORY_LABELS[category] ?? category;
}
export const policySchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    mode: policyModeSchema.optional(),
    supportsEnforce: z.boolean().optional(),
    enforceDisabledReason: enforceDisabledReasonSchema.optional(),
    enforceTooltip: z.string().optional(),
    usesSeverityThreshold: z.boolean().optional(),
    hasSettings: z.boolean(),
    settingsCount: z.number(),
    customizedSettings: z.number(),
    category: z.string(),
    settings: z.record(z.string(), z.any()).optional(),
    // KIR-3973 / KIR-3861: re-evaluation-freshness key (schema dk2). Changes when
    // re-processing the same content would differ — either a different finding set
    // (scanner code / ruleset / this policy's org config) OR a different
    // enforcement action (policy mode Off/Monitor/Enforce + org enforcement
    // severity threshold; secret-confidence for secret-detection). Because
    // enforcement is per-org, no policy's key is org-agnostic. Clients throw their
    // file cache when it changes so already-scanned files are re-evaluated.
    detectionKey: z.string().optional(),
    // KIR-3800 (Security Groups): on a GROUP-scoped policy list, `true` when this
    // policy's mode or settings are set at the group level (overriding the org
    // default), `false` when the group inherits the org value. Absent on
    // organization/account scopes (nothing to inherit from).
    isOverridden: z.boolean().optional(),
});
export const policyListSchema = z.object({
    policies: z.array(policySchema),
    // KIR-3973: one org-wide composite over every policy's detectionKey — a single
    // "did any detection input change?" signal to poll instead of diffing each key.
    compositeDetectionKey: z.string().optional(),
    // KIR-4482: block keys of the org's MCP servers blocked from Inventory —
    // each server's resolved identity without its version (`npm:<package>`,
    // `http:<host>[/<path digest>]`, `git:<repo>`, …) or `name:<config name>`
    // where none was resolved. Sent to org API-key callers only, while
    // `mcp_inventory_enforcement` is on; the endpoint hook denies the tools of
    // a configured server whose key is listed.
    blockedMcpServerKeys: z.array(z.string()).optional(),
});
// Response of GET /api/policies, the client read-proxy the endpoint agent and
// the IDE extensions poll. KIR-5293: scanRequestBodyLimitBytes is the largest
// POST /api/scans body the backend accepts; the agent doesn't read or send a
// file whose scan request exceeds it.
export const clientPolicyListSchema = policyListSchema.extend({
    scanRequestBodyLimitBytes: z.number().int().positive().optional(),
});
/*
  Policy toggle schemas
  Supports both legacy { enabled } and new { mode } for backward compatibility
*/
export const policyToggleSchema = z.object({
    enabled: z.boolean().optional(),
    mode: policyModeSchema.optional(),
});
export const policyToggleResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy settings schemas
*/
export const policySettingSchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    /*
      `mode` is a nested Off/Monitor/Enforce sub-policy control (KIR-4426): a
      setting whose value is a PolicyMode rather than a flag, so a sub-policy can
      enforce while its parent policy stays on Monitor. Renders as the same
      tri-state selector the parent policy uses, not a checkbox.
    */
    type: z.enum(['boolean', 'string', 'array', 'object', 'mode']),
    defaultValue: z.any(),
    value: z.any(),
    isCustomized: z.boolean(),
    group: z.string().optional(),
    validation: z
        .object({
        pattern: z.string().optional(),
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
        required: z.boolean().optional(),
        arrayItemType: z.enum(['string', 'object']).optional(),
        properties: z.record(z.string(), z.any()).optional(),
    })
        .optional(),
});
export const policySettingsSchema = z.object({
    policy: z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        supportsEnforce: z.boolean().optional(),
        enforceDisabledReason: enforceDisabledReasonSchema.optional(),
        enforceTooltip: z.string().optional(),
    }),
    settings: z.array(policySettingSchema),
});
/*
  Individual policy settings schemas
  Each policy has its own strict schema that prevents prototype pollution
*/
// YOLO Modes settings
const yoloModesSettingsSchema = z
    .object({
    // Cursor settings
    enableAutoRun: z.boolean().optional(),
    enableAutoFixDetection: z.boolean().optional(),
    enableAutoApplyDetection: z.boolean().optional(),
    // VSCode Copilot settings
    enableAgentAutoFix: z.boolean().optional(),
    enableGlobalAutoApprove: z.boolean().optional(),
    enableTerminalAutoApprove: z.boolean().optional(),
    enableMcpDiscovery: z.boolean().optional(),
    enableAutoAcceptDelay: z.boolean().optional(),
})
    .strict();
// MCP Server Sources settings
const mcpServerSourcesSettingsSchema = z
    .object({
    trustedGitRepositories: z.array(z.string()).optional(),
    trustedSources: z.array(z.string()).optional(),
    // KIR-3725 removed `strictMode` and `allowLocalhostHttp`: both only ever
    // gated the retired "Untrusted MCP Server" detection. Stored settings JSON
    // that still carries them is untouched — this schema validates writes only.
    blockedListMode: policyModeSchema.optional(),
})
    .strict();
// MCP Security settings (combines dependencies and tool descriptions settings)
const mcpSecuritySettingsSchema = z
    .object({
    // Scan enablement flags
    enableHiddenCharactersScan: z.boolean().optional(),
    enableDependenciesScan: z.boolean().optional(),
    enableToolDescriptionsScan: z.boolean().optional(),
    // KIR-3157: scan MCP tool-call responses for hidden unicode.
    enableToolResponseScan: z.boolean().optional(),
    // Dependencies settings
    excludedPackages: z.array(z.string()).optional(),
    scanRecursiveDependencies: z.boolean().optional(),
    // Tool descriptions settings
    suspiciousPatterns: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string(),
        severity: severitySchema,
    }))
        .optional(),
    enableAIToolAnalysis: z.boolean().optional(),
})
    .strict();
// Hidden Characters settings (no settings defined)
const hiddenCharactersSettingsSchema = z.object({}).strict();
// AI Rules / instructions policy settings (aligns with AI_INSTRUCTIONS_SETTINGS)
const aiInstructionsSettingsSchema = z
    .object({
    checkRules: z.boolean().optional(),
    checkSkills: z.boolean().optional(),
    checkCommands: z.boolean().optional(),
    // KIR-4743: `checkHooks` shipped in the backend registry (AI_INSTRUCTIONS_SETTINGS)
    // and in the scanner, but not here — and this object is `.strict()`, so the
    // dashboard form, which always posts all four toggles, had EVERY save on the
    // AI Instructions card rejected with "Unrecognized key(s): 'checkHooks'".
    checkHooks: z.boolean().optional(),
})
    .strict();
/*
  KIR-4928: one Chat Security rule list. The schema checks only the shape.
  Whether a pattern is usable (passes the ReDoS safety screen and compiles) is
  checked on the backend at save time, and only for rules being added: it has
  to compare against the stored rules, which a schema cannot see. A stored rule
  that fails the check does not block saving the policy; the dashboard marks it
  "Never matches". See `assertNoNewUnusableChatSecurityRules` in the backend.
*/
const chatSecurityPatternArraySchema = z.array(z.object({
    pattern: z.string(),
    reason: z.string().optional(), // tolerate legacy naming from frontend
    description: z.string().optional(),
    severity: severitySchema,
}));
// Cursor Database settings (aligns with CURSOR_CHAT_SETTINGS)
// Pattern merging logic:
// - CODE BLOCKS: maliciousPatterns + credentialPatterns (merged in scanner)
// - COMMANDS: commandPatterns (with fallback to maliciousPatterns)
const cursorDatabaseSettingsSchema = z
    .object({
    // Primary pattern field for code block scanning
    maliciousPatterns: chatSecurityPatternArraySchema.optional(),
    // Legacy credential patterns (merged with maliciousPatterns for code scanning)
    credentialPatterns: chatSecurityPatternArraySchema.optional(),
    // Command-specific patterns
    commandPatterns: chatSecurityPatternArraySchema.optional(),
    aiCodeSensitivity: severitySchema.optional(),
    enableCursorAIAnalysis: z.boolean().optional(),
    /*
      KIR-4426: nested Real-Time Tool-Call Analysis control. A PolicyMode, not
      a flag, so an admin can leave the static Command/Code rules on Monitor
      and still let real-time analysis block the riskiest calls. The parent
      policy being Off still hard-disables it (see resolveToolCallScanMode).
    */
    toolCallScanMode: policyModeSchema.optional(),
    /*
      Tolerated, never read: the pre-release boolean shape of the control above.
      A settings save is a FULL REPLACE and this object is `.strict()`, so a
      browser tab loaded before the rename would otherwise 400 the WHOLE
      cursor-database save — taking the live Command/Code rules down with it
      for the length of a deploy. Accepting and ignoring the key keeps that
      save working; resolveToolCallScanMode reads only `toolCallScanMode`, so a
      stale `true` cannot switch the scan on. Delete once no deployed client
      sends it.
    */
    enableToolCallScan: z.boolean().optional(),
})
    .strict();
const extensionsManagementExtensionListSchema = z.preprocess(stripExtensionListUiPlaceholders, z.array(extensionEntrySchema).optional());
// Extensions Management settings (allow/block lists are now in policy_exceptions)
const extensionsManagementSettingsSchema = z
    .object({
    autoRemoveBlockedExtensions: z.boolean().optional(),
    blockedListMode: policyModeSchema.optional(),
    // Legacy fields — migrated to policy_exceptions (migration 0071).
    // Retained so older agents that read settings don't break .strict() validation.
    defaultBehavior: z.string().optional(),
    allowedExtensions: extensionsManagementExtensionListSchema,
    blockedExtensions: extensionsManagementExtensionListSchema,
    allowedPublishers: z.array(publisherNameSchema).optional(),
    blockedPublishers: z.array(publisherNameSchema).optional(),
})
    .strict();
/**
 * Shape of a Gitleaks rule ID (e.g. `aws-access-token`, `generic-api-key`).
 * Mirrors upstream gitleaks rule IDs: alphanumeric start, then alphanumerics,
 * dots, underscores and dashes. Anchored so it can be embedded safely in the
 * generated TOML without any quoting/escaping logic.
 */
export const GITLEAKS_RULE_ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,127}$/i;
/** Upper bound on `disabledGitleaksRules` (upstream ships ~222 rules). */
export const MAX_DISABLED_GITLEAKS_RULES = 500;
// Secret Detection settings (aligns with SECRET_DETECTION_SETTINGS)
const secretDetectionSettingsSchema = z
    .object({
    customPatterns: z
        .array(z
        .object({
        pattern: z.string(),
        severity: severitySchema,
        reason: z.string().optional(), // tolerate legacy naming from frontend
        description: z.string().optional(),
    })
        .transform(data => {
        // Map reason to description if description is missing (for backward compatibility)
        return {
            ...data,
            description: data.description ?? data.reason ?? '',
        };
    })
        .refine(data => data.description && data.description.length > 0, {
        message: 'Description is required',
    }))
        .optional(),
    preventSecretLeakage: z.boolean().optional(),
    secretFilePatterns: z
        .array(z.object({
        pattern: z.string(),
        severity: severitySchema,
        description: z.string(),
    }))
        .optional(),
    /**
     * Built-in Gitleaks rule IDs the org has turned off (KIR-4396). The agent
     * and the IDE extension materialise these into a gitleaks overlay config
     * (`[extend] disabledRules`) and pass it via `--config`.
     *
     * Deliberately NOT a `z.enum` over the known rule IDs: a later gitleaks
     * upgrade that removes a rule would then hard-reject an org's saved
     * settings. Unknown IDs are inert in gitleaks (an entry in `disabledRules`
     * that matches no rule is simply ignored), so they are accepted here;
     * `GITLEAKS_DEFAULT_RULE_ID_SET` is what a UI should check against to warn
     * an admin that an ID no longer exists.
     */
    disabledGitleaksRules: z
        .array(z.string().regex(GITLEAKS_RULE_ID_PATTERN))
        .max(MAX_DISABLED_GITLEAKS_RULES)
        .optional(),
})
    .strict();
// PII Detection settings (aligns with PII_DETECTION_SETTINGS; KIR-3034, KIR-3837)
const piiDetectionSettingsSchema = z
    .object({
    // Enabled + scanned DLP infoTypes. Types not in this list are never scanned.
    infoTypes: z.array(z.string()).optional(),
    // All DLP infoTypes the admin has added to the table via the picker, incl.
    // ones currently toggled OFF — so an off row (e.g. an added higher-latency
    // type) persists as a visible row without being scanned (KIR-3837).
    selectedInfoTypes: z.array(z.string()).optional(),
    // Curated infoTypes the admin removed from the table (KIR-4902). Curated
    // rows otherwise always render; Reset to Defaults clears this list.
    removedInfoTypes: z.array(z.string()).optional(),
    // Additional patterns -> DLP custom infoTypes. `severity` is optional: the
    // scanner derives severity from the volume tier + sensitivity and treats
    // every custom pattern as sensitive (it is not stored per-pattern).
    customPatterns: z
        .array(z.object({
        pattern: z.string(),
        severity: severitySchema.optional(),
        description: z.string(),
    }))
        .optional(),
    // DLP minLikelihood threshold.
    minLikelihood: z
        .enum(['VERY_UNLIKELY', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'VERY_LIKELY'])
        .optional(),
    // Which ingress sources to scan.
    scanPrompt: z.boolean().optional(),
    scanFileReads: z.boolean().optional(),
    scanMcpOutputs: z.boolean().optional(),
    scanTerminalOutput: z.boolean().optional(),
    // Configurable alert-tier boundaries (KIR-3837). Stored as digit strings to
    // match the generic string-setting renderer; the scanner coerces to a number.
    multipleThreshold: z
        .string()
        .regex(/^[1-9]\d*$/)
        .optional(),
    bulkThreshold: z
        .string()
        .regex(/^[1-9]\d*$/)
        .optional(),
    // Whether findings with no sensitive item can still alert, and at what
    // severity. 'off' = no alert (KIR-3837 default).
    nonSensitiveAlertSeverity: z.enum(['off', 'low', 'medium']).optional(),
})
    .strict();
/**
 * KIR-4443. No settings: the package Block list is fixed at Enforce, so unlike
 * extensions-management there is no `blockedListMode` to write. Strict, so a
 * write that tries is a 400 rather than a 200 the backend then ignores.
 */
const packagesManagementSettingsSchema = z.object({}).strict();
/**
 * KIR-4699. No settings, for the same reason as packages above: the skill
 * Block list is fixed at Enforce and is managed only from Inventory → Skills,
 * which has no mode control. Strict, so a write that tries is a 400 rather
 * than a 200 the backend then ignores.
 */
const skillsManagementSettingsSchema = z.object({}).strict();
/** KIR-4443. The two sub-policy toggles; both follow the policy's mode. */
const maliciousPackagesSettingsSchema = z
    .object({
    maliciousPackagesTyposquatting: z.boolean().optional(),
    maliciousPackagesKnownMalware: z.boolean().optional(),
})
    .strict();
/*
  Policy settings update - discriminated union by policyKey
  This provides full type safety and automatic validation per policy
*/
export const policySettingsUpdateSchema = z.discriminatedUnion('policyKey', [
    z.object({
        policyKey: z.literal(PolicyKey.YoloModes),
        settings: yoloModesSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.ExtensionSecurity),
        settings: extensionSecuritySettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.McpSecurity),
        settings: mcpSecuritySettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.McpServerSources),
        settings: mcpServerSourcesSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.HiddenCharacters),
        settings: hiddenCharactersSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.CursorDatabase),
        settings: cursorDatabaseSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.AiInstructions),
        settings: aiInstructionsSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.SecretDetection),
        settings: secretDetectionSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.ExtensionsManagement),
        settings: extensionsManagementSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.PiiDetection),
        settings: piiDetectionSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.MaliciousPackages),
        settings: maliciousPackagesSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.PackagesManagement),
        settings: packagesManagementSettingsSchema,
    }),
    z.object({
        policyKey: z.literal(PolicyKey.SkillsManagement),
        settings: skillsManagementSettingsSchema,
    }),
]);
export const policySettingsUpdateResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy settings delete result schema
*/
export const policySettingsDeleteResultSchema = z.object({
    success: z.boolean(),
});
/*
  Enforcement severity threshold schemas
  Global setting that controls at which severity level enforcement actions trigger
*/
export const enforcementSeverityThresholdSchema = z.object({
    severity: severitySchema,
});
export const enforcementSeverityThresholdResultSchema = z.object({
    success: z.boolean(),
});
/*
  Policy edit context schemas
  Used by the "Edit Policy" action on the Alerts screen
*/
export const policyEditContextQuerySchema = z.object({
    subPolicyKey: z.string().optional(),
    alertKey: z.string().optional(),
});
export const policyEditContextSchema = z.object({
    policy: z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        mode: policyModeSchema,
        supportsEnforce: z.boolean().optional(),
        enforceDisabledReason: enforceDisabledReasonSchema.optional(),
        enforceTooltip: z.string().optional(),
    }),
    subPolicy: z
        .object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        // Mirrors policySettingSchema.type — see the `mode` note there.
        type: z.enum(['boolean', 'string', 'array', 'object', 'mode']),
        defaultValue: z.any(),
        value: z.any(),
        isCustomized: z.boolean(),
    })
        .nullable(),
    version: z.number(),
});
/*
  Versioned policy toggle schema
  Extends policyToggleSchema with optional expectedVersion for conflict detection
*/
export const policyToggleVersionedSchema = z.object({
    enabled: z.boolean().optional(),
    mode: policyModeSchema.optional(),
    expectedVersion: z.number().optional(),
    source: z.enum(['policies', 'alerts']).optional(),
    alertKey: z.string().optional(),
});
/*
  Versioned policy settings update schema
  Adds optional expectedVersion and source metadata for conflict detection and auditing
*/
export const policySettingsUpdateVersionedMetaSchema = z.object({
    expectedVersion: z.number().optional(),
    source: z.enum(['policies', 'alerts']).optional(),
    alertKey: z.string().optional(),
});
//# sourceMappingURL=policies.js.map